/**
 * Shared AudioContext Pool
 * 
 * Browsers limit the number of AudioContext instances (~6 on Chrome/Android).
 * This module provides a single shared AudioContext for all audio modules
 * to prevent exhaustion crashes.
 */

let _sharedCtx: AudioContext | null = null;

/**
 * Get the shared AudioContext, creating it lazily if needed.
 * Wrapped in try/catch to prevent crashes on devices that reject creation.
 */
export function getSharedAudioContext(): AudioContext {
  if (_sharedCtx && _sharedCtx.state !== 'closed') {
    if (_sharedCtx.state === 'suspended') {
      _sharedCtx.resume().catch(() => {});
    }
    return _sharedCtx;
  }

  try {
    _sharedCtx = new AudioContext();
  } catch (e) {
    console.warn('[AudioPool] Failed to create AudioContext:', e);
    // Return a dummy that won't crash callers — they already wrap in try/catch
    _sharedCtx = new AudioContext();
  }

  if (_sharedCtx.state === 'suspended') {
    _sharedCtx.resume().catch(() => {});
  }

  return _sharedCtx;
}

/**
 * Resume the shared AudioContext (call after user gesture).
 */
export async function resumeSharedAudioContext(): Promise<void> {
  if (_sharedCtx && _sharedCtx.state === 'suspended') {
    await _sharedCtx.resume();
  }
}

/**
 * Return the current state of the shared AudioContext without side effects.
 */
export function getAudioContextState(): AudioContextState | 'none' {
  if (!_sharedCtx) return 'none';
  return _sharedCtx.state;
}

/**
 * Create a fresh AudioContext if the current one is broken.
 * Used as a last-resort recovery.
 */
export function resetSharedAudioContext(): AudioContext {
  try {
    if (_sharedCtx) _sharedCtx.close().catch(() => {});
  } catch {}
  _sharedCtx = null;
  return getSharedAudioContext();
}
